import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { RoleBadge } from "@/components/users/role-badge.tsx";
import {
  User,
  useClearUserCache,
  useDevCleanupUser,
} from "@/lib/api/user.ts";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import {
  getMenteeDisplayName,
  getMenteeDisplayPhoto,
  isFieldRedacted,
} from "@/lib/redacted-display";
import {
  getPrimaryRole,
  isMentorIdentity,
} from "@/lib/utils/system-roles";
import { getInitials } from "@/lib/utils.ts";
import { ChevronLeftIcon } from "@radix-ui/react-icons";
import { useRouter } from "@tanstack/react-router";
import { format } from "date-fns";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

interface HeaderProps {
  user: User;
}

export function Header({ user }: HeaderProps) {
  const router = useRouter();
  const ability = useAbility();
  const clearUserCacheMutation = useClearUserCache();
  const devCleanupUserMutation = useDevCleanupUser();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const { name, redacted_fields } = user;
  const isNameRedacted = isFieldRedacted("name", redacted_fields, name);
  const displayName = getMenteeDisplayName(user);
  const displayPhoto = getMenteeDisplayPhoto(user);
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canClearUserCache =
    hasPrivilegedAccess && ability.can("update", "users");
  const canManageAll = ability.can("manage", "all");

  const isNonProduction =
    import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "production";
  const canResetUser = isNonProduction && canManageAll;

  const handleClearCache = () => {
    clearUserCacheMutation.mutate(user.id);
  };

  const handleResetUser = async () => {
    devCleanupUserMutation.mutate(user.id, {
      onSuccess: () => {
        setShowResetDialog(false);
      },
    });
  };

  const handleNavigateToMentorDashboard = () => {
    router.navigate({
      to: "/mentor-data-dashboard",
      search: { mentorId: user.id },
    });
  };

  return (
    <div className="flex flex-row items-center justify-between gap-2 border-b pb-3">
      <div className="flex flex-row items-center gap-2">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            router.history.back();
          }}
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </Button>

        <div className="flex items-center gap-2">
          <Avatar className="h-10 w-10">
            <AvatarImage src={displayPhoto} alt={displayName} />
            <AvatarFallback className="text-xs">
              {isNameRedacted ? displayName : getInitials(name ?? "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1">
            <div className="font-bold">{displayName}</div>
            <div className="flex flex-row items-center gap-2">
              <RoleBadge
                role={getPrimaryRole(user.systemRoles)}
                size="sm"
              />
              <div className="text-xs font-normal italic leading-tight text-zinc-600">
                Started on {format(new Date(user.created_at), "MMM dd, yyyy")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isMentorIdentity(user.systemRoles) && (
          <Button
            variant="default"
            size="sm"
            onClick={handleNavigateToMentorDashboard}
            className="flex w-36 items-center justify-center gap-2 px-3 py-2 text-white"
            style={{
              borderRadius: "var(--radius-med, 10px)",
              background:
                "var(--Components-Colors-Buttons-btn-primary, #1C1C1C)",
            }}
          >
            View Performance
          </Button>
        )}
        {canClearUserCache && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearCache}
            disabled={clearUserCacheMutation.isPending}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {clearUserCacheMutation.isPending ? "Clearing..." : "Clear Cache"}
          </Button>
        )}
        {canResetUser && (
          <>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowResetDialog(true)}
              disabled={devCleanupUserMutation.isPending}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              {devCleanupUserMutation.isPending ? "Resetting..." : "Reset User"}
            </Button>
            <Dialog
              open={showResetDialog}
              onOpenChange={(open) => {
                if (!open) setShowResetDialog(false);
              }}
            >
              <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
                <div className="p-4">
                  <DialogHeader>
                    <DialogTitle className="mb-1 text-2xl font-semibold">
                      Reset User Data
                    </DialogTitle>
                    <DialogDescription className="text-base">
                      This will delete all user data including mentorships,
                      messages, and profile information. This action cannot be
                      undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 flex flex-row items-center gap-4">
                    <Button
                      className="w-full text-lg font-bold text-slate-900"
                      variant="outline"
                      onClick={() => setShowResetDialog(false)}
                      disabled={devCleanupUserMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-full text-lg font-bold text-white"
                      variant="destructive"
                      onClick={handleResetUser}
                      disabled={devCleanupUserMutation.isPending}
                    >
                      {devCleanupUserMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Reset User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
